import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const InflationCalculatorPage = () => {
  const [calcMode, setCalcMode] = useState('future'); // future, past, rate
  const [amount, setAmount] = useState('1000');
  const [rate, setRate] = useState('3');
  const [years, setYears] = useState('10');
  const [futureAmount, setFutureAmount] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const amt = parseFloat(amount) || 0;
    const r = (parseFloat(rate) || 0) / 100;
    const y = parseFloat(years) || 1;
    const future = parseFloat(futureAmount) || 0;

    if (calcMode === 'future') {
      // What will $X be worth in Y years?
      const adjustedValue = amt / Math.pow(1 + r, y);
      const purchasingPowerLost = amt - adjustedValue;
      setResult({
        type: 'future',
        original: amt,
        adjusted: adjustedValue,
        lost: purchasingPowerLost,
        lostPercent: ((purchasingPowerLost / amt) * 100).toFixed(1),
        years: y,
        rate: rate
      });
    } else if (calcMode === 'past') {
      // How much would $X from Y years ago be worth today?
      const adjustedValue = amt * Math.pow(1 + r, y);
      const gained = adjustedValue - amt;
      setResult({
        type: 'past',
        original: amt,
        adjusted: adjustedValue,
        gained,
        gainedPercent: ((gained / amt) * 100).toFixed(1),
        years: y,
        rate: rate
      });
    } else if (calcMode === 'rate') {
      // What inflation rate causes $X to become $Y in Z years?
      const calculatedRate = (Math.pow(future / amt, 1 / y) - 1) * 100;
      setResult({
        type: 'rate',
        original: amt,
        future,
        rate: calculatedRate.toFixed(2),
        years: y
      });
    }
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Inflation Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Inflation Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate the impact of inflation on purchasing power over time.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex flex-wrap border-b border-[#2a2a2a] mb-6 gap-1">
              {[{ id: 'future', label: 'Future Value' }, { id: 'past', label: 'Past Value' }, { id: 'rate', label: 'Find Rate' }].map(m => (
                <button key={m.id} onClick={() => setCalcMode(m.id)}
                  className={`px-4 py-2 font-medium text-sm border-b-2 ${calcMode === m.id ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                  {m.label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">{calcMode === 'past' ? 'Past Amount' : 'Current Amount'}</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>

              {calcMode === 'rate' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Future Amount</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={futureAmount} onChange={(e) => setFutureAmount(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>
              )}

              {calcMode !== 'rate' && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="text-gray-300 w-32">Inflation Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Time Period</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={years} onChange={(e) => setYears(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">years</span>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                {result.type === 'future' && (
                  <div className="text-center">
                    <p className="text-gray-400 mb-2">{formatCurrency(result.original)} today will have the purchasing power of</p>
                    <p className="text-4xl font-bold text-red-400">{formatCurrency(result.adjusted)}</p>
                    <p className="text-gray-500 mt-2">in {result.years} years at {result.rate}% inflation</p>
                    <p className="text-red-400 mt-4">Purchasing power lost: {formatCurrency(result.lost)} ({result.lostPercent}%)</p>
                  </div>
                )}
                {result.type === 'past' && (
                  <div className="text-center">
                    <p className="text-gray-400 mb-2">{formatCurrency(result.original)} from {result.years} years ago equals</p>
                    <p className="text-4xl font-bold text-green-400">{formatCurrency(result.adjusted)}</p>
                    <p className="text-gray-500 mt-2">in today's dollars at {result.rate}% inflation</p>
                    <p className="text-green-400 mt-4">Equivalent increase: {formatCurrency(result.gained)} ({result.gainedPercent}%)</p>
                  </div>
                )}
                {result.type === 'rate' && (
                  <div className="text-center">
                    <p className="text-gray-400 mb-2">Inflation rate needed for {formatCurrency(result.original)} to become {formatCurrency(result.future)}</p>
                    <p className="text-4xl font-bold text-cyan-400">{result.rate}%</p>
                    <p className="text-gray-500 mt-2">over {result.years} years</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">About Inflation</h2>
              <p className="text-gray-400 leading-relaxed">
                Inflation is the rate at which the general level of prices for goods and services rises, causing purchasing 
                power to fall. The average historical inflation rate in the US is about 3% per year.
              </p>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Interest', path: '/interest-calculator' }, { name: 'Investment', path: '/investment-calculator' }, { name: 'Retirement', path: '/retirement-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InflationCalculatorPage;
